// src/context/FeatureContext.js
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const FeatureContext = createContext();

export const FeatureProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [enabledFeatures, setEnabledFeatures] = useState(new Set());
  const abortControllerRef = useRef(null);
  const lastUserIdRef = useRef(null);

  const fetchFeatures = useCallback(async (featuresToCheck) => {
    // Don't fetch features if user is not authenticated or loading
    if (!user || loading) {
      setEnabledFeatures(new Set());
      return;
    }

    // Check if user ID has changed - if not, don't refetch
    if (lastUserIdRef.current === user.id) {
      return;
    }

    // Additional check for logout state
    if (typeof window !== 'undefined' && window.lastLogoutAt && Date.now() - window.lastLogoutAt < 3000) {
      setEnabledFeatures(new Set());
      return;
    }

    // Cancel any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const fetched = new Set();
    
    try {
      for (const feature of featuresToCheck) {
        // Check if request was cancelled
        if (abortControllerRef.current.signal.aborted) {
          break;
        }

        // Check again for logout state before each request
        if (typeof window !== 'undefined' && window.lastLogoutAt && Date.now() - window.lastLogoutAt < 3000) {
          break;
        }

        try {
          const token = localStorage.getItem('token') || sessionStorage.getItem('token');
          if (!token) {
            break;
          }

          const res = await fetch(`/api/accounts/feature-check/?feature=${feature}`, {
            signal: abortControllerRef.current.signal,
            headers: {
              'Authorization': `Token ${token}`
            }
          });
          
          if (res.ok) {
            const data = await res.json();
            if (data.enabled) {
              fetched.add(feature);
            }
          } else if (res.status === 401 || res.status === 403) {
            // Unauthorized or Forbidden - stop fetching features
            break;
          }
        } catch (err) {
          if (err.name === 'AbortError') {
            break;
          }
          console.error(`Failed to check feature "${feature}":`, err);
        }
      }
      
      // Only update state if request wasn't cancelled
      if (!abortControllerRef.current.signal.aborted) {
        setEnabledFeatures(fetched);
        lastUserIdRef.current = user.id;
      }
    } catch (error) {
      console.error('[FeatureContext] Error fetching features:', error);
    }
  }, [user?.id, loading]); // Only depend on user.id, not the entire user object

  useEffect(() => {
    // Clear features immediately if no user or still loading
    if (!user || loading) {
      setEnabledFeatures(new Set());
      lastUserIdRef.current = null;
      return;
    }

    const featuresToCheck = [
      'worksheet_uploads',
      'performance_analytics',
      'revision_notes',
      // Add any future feature codes here
    ];
    
    fetchFeatures(featuresToCheck);

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchFeatures]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const hasFeature = useCallback((featureCode) => enabledFeatures.has(featureCode), [enabledFeatures]);

  return (
    <FeatureContext.Provider value={{ hasFeature, enabledFeatures }}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatures = () => useContext(FeatureContext);
